import { useFetch } from "modules/main/hooks/useFetch";
import { FetchDataOptions } from "modules/main/services/fetchData";
import { useLocation } from "react-router-dom";
import { CartCreationResponse} from "../types/cartCreationResponse";
import { cartCreation, cartCreationResume } from "modules/main/services/endpoints";
import apiClient from "modules/main/services/apiClient";
import { useContext, useEffect, useState } from "react";

import { AppDataContext } from "modules/main/context/appData";
import { CartCreationStreamStatus } from "../components/CartCreationStreamStatus";
import { CartCreationStoreView } from "../components/CartCreationStoreList";
import { AutomatizationStep } from "../types/cartAutomatizationStatus";
import { RemoteBrowser } from "../components/RemoteBrowser";
import { CartCreationMissedItems } from "../components/CartCreationMissedItems";
import { getStoreBdData } from "utils/storeDataManager";
import { Skeleton } from "@/components/ui/skeleton";
import { MainLogo } from "modules/main/assets/main_logo"
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Info } from "lucide-react";
import { cn } from "@/lib/utils";
import { useMediaQuery } from "@/hooks/use-media-query";
import { isDesktopSize } from "utils/is-desktop";
import { selectScenario } from "../services/selectScenario";
import { usePageTitle } from "hooks/usePageTitle";





export const CartCreation = () => {
    const location = useLocation();
    const {setTitle,setShowBack } = usePageTitle();
    const params = new URLSearchParams(location.search);
    const scenario_id = params.get("scenario_id") ? Number(params.get("scenario_id")) : 0;
    const store_id = params.get("store_id") ? Number(params.get("store_id")) : undefined;
    const isMobile = !useMediaQuery(isDesktopSize);
    const [openBrowser,setOpenBrowser] = useState(false);
    const [openMissedItems,setOpenMissedItems] = useState(false);
    const {storeList} = useContext(AppDataContext); 
    const [storesStatus, setStoresStatus] = useState<AutomatizationStep[]>([]);
    const [remoteStoreBrowserStoreId, setRemoteStoreBrowserId] = useState<number | null>(null);
    const [remoteBrowserToken, setRemoteBrowserToken] = useState<string>("");
    const [refetchTick, setRefetchTick] = useState(0);
    const [resumingStores, setResumingStores] = useState<Set<number>>(new Set());
    const [resumeErrorsByStore, setResumeErrorsByStore] = useState<Record<number, string>>({});
    const [sessionExpired, setSessionExpired] = useState(false);
    const [streamingTtlUpdate, setStreamingTtlUpdate] = useState<{ store_id: number; newExpiresAt: string; remainingSeconds: number } | null>(null);

    useEffect(() => {
      setTitle("");
      setShowBack(true);
    }, [setTitle]);

    // Limpiar tiendas resuming cuando el backend informa un cambio de estado
    useEffect(() => {
        const pausedStatuses: string[] = ['LOGIN_REQUIRED', 'ADDRESS_NOT_SELECTED', 'FAILED'];

        setResumingStores(prev => {
            const next = new Set(prev);
            let changed = false;
            for (const storeId of prev) {
                const current = storesStatus.find(s => s.store_id === storeId);
                if (current && !pausedStatuses.includes(current.status)) {
                    next.delete(storeId);
                    changed = true;
                }
            }
            return changed ? next : prev;
        });

        setResumeErrorsByStore(prev => {
            const updated = { ...prev };
            let changed = false;

            Object.keys(prev).forEach((storeIdKey) => {
                const storeId = Number(storeIdKey);
                const current = storesStatus.find(s => s.store_id === storeId);
                if (current && !pausedStatuses.includes(current.status)) {
                    delete updated[storeId];
                    changed = true;
                }
            });

            return changed ? updated : prev;
        });
    }, [storesStatus]);

    const requestOptions:FetchDataOptions = {
        method:"POST",
        headers:{"content-type":"application/json"},
        body: {
            scenario_id:scenario_id,
            store_id: store_id
        }
    }
    // console.log("Requesting cart creation with scenario_id:", scenario_id);
    const {data,error,loading} = useFetch<CartCreationResponse>(cartCreation,requestOptions);


    /**
     * Restarts the full cart creation process from scratch.
     * Resets store statuses and re-calls the cart creation endpoint.
     */
    const retryProcess = async () => {
        setStoresStatus([]);
        await apiClient.post(cartCreation, { scenario_id, store_id });
    };

    /**
     * Marks the current cart creation scenario as selected when checkout starts.
     * @param scenarioId - Current scenario id, also used as backend order id.
     */
    const handleSelectScenario = async (scenarioId: number) => {
        if (scenarioId <= 0) {
            return;
        }

        await selectScenario(scenarioId);
    };

    const openBrowserHandler = (store_id:number, token:string) => {
        setRemoteStoreBrowserId(store_id);
        setRemoteBrowserToken(token);
        setOpenBrowser(true);
    }

    const reportResumeError = (error: unknown, storeId: number) => {
        if (typeof window !== "undefined" && "errorReporting" in window) {
            (window as Window & {
                errorReporting?: { captureException: (exception: unknown, context?: unknown) => void };
            }).errorReporting?.captureException(error, {
                tags: { scope: "cartCreation", action: "resume" },
                extra: { storeId, scenarioId: scenario_id }
            });
        }
    }

    const clearStoreResumeError = (storeId: number) => {
        setResumeErrorsByStore(prev => {
            if (!prev[storeId]) return prev;
            const updated = { ...prev };
            delete updated[storeId];
            return updated;
        });
    }

    const setStoreResumeError = (storeId: number, message: string) => {
        setResumeErrorsByStore(prev => ({
            ...prev,
            [storeId]: message
        }));
    }

    /**
     * Retry handler for stores in FAILED state.
     * Marks the store as resuming and calls resumeCartCreation.
     * @param store_id - The store to retry.
     */
    const retryFailedStore = async (store_id: number) => {
        setResumingStores(prev => new Set(prev).add(store_id));
        await resumeCartCreation(store_id);
    };

    const resumeCartCreation = async (store_id:number) => {
        //implementar logica para reanudar la creacion del carrito
        if (!store_id) return;
        clearStoreResumeError(store_id);

        try{
            const resumeCart = await fetch(cartCreationResume, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    scenario_id:scenario_id,
                    store_id: store_id
                }),
                credentials: "include"                          
            });
            if(!resumeCart.ok){
                if(resumeCart.status === 400){
                    const errorData = await resumeCart.json().catch(() => null);
                    if(errorData?.detail === "PROCCESS NOT FOUND"){
                        setResumingStores(prev => {
                            const next = new Set(prev);
                            next.delete(store_id);
                            return next;
                        });
                        setSessionExpired(true);
                        return;
                    }
                }
                throw new Error("Failed to resume cart creation");
            }
        }catch(error){
            setResumingStores(prev => {
                const next = new Set(prev);
                next.delete(store_id);
                return next;
            });
            setStoreResumeError(store_id, "No pudimos reanudar esta tienda. Intenta nuevamente.");
            reportResumeError(error, store_id);
        }          
    }

    const cleanupRemoteBrowserState = () => {
        setRemoteStoreBrowserId(null);
        setRemoteBrowserToken("");
        setOpenBrowser(false);
    }

    const closeBrowserHandler = (open:boolean) => {
        if(open === false){
            cleanupRemoteBrowserState();
            return;
        }

        setOpenBrowser(open);
    }

    const confirmSessionReadyHandler = () => {
        const storeId = remoteStoreBrowserStoreId;

        if (storeId) {
            setResumingStores(prev => new Set(prev).add(storeId));
            resumeCartCreation(storeId);
        }

        cleanupRemoteBrowserState();
    }

    if(loading){
        return <CartCreationLoading/>
    }

    // console.log("Cart creation response:", data, error);

    if(error){
        return <CartCreationError error={error}/>
    }

    return(
    <>
    <main>
        <div className={cn("flex flex-row  gap-2 py-4 px-6  lg:p-8 flex-wrap justify-center",{
            "w-full":isMobile,
            "w-2/5 mx-auto": !isMobile
        })}>
            <CartCreationStreamStatus 
                    setStatus={setStoresStatus} 
                    status={storesStatus} 
                    setOpenMissedItemsDialog={setOpenMissedItems} 
                    setRefreshTik={setRefetchTick} 
                    storeData={storeList}
                    resumeErrorsByStore={resumeErrorsByStore}
                    onRetryResume={resumeCartCreation}
                    resumingStores={resumingStores}
                    onRetryProcess={retryProcess}
                    onStreamingTtlUpdate={setStreamingTtlUpdate}
                    className="w-full"/>        
        {
            data && data.storeList.length > 0 && (
                <div className="flex flex-col gap-4 justify-between w-full">
                    <CartCreationStoreView 
                        scenarioId={scenario_id}
                        storeList={data.storeList}
                        storeData={storeList} storeCreationStatus={storesStatus} setOpenBrowser={openBrowserHandler} resumingStores={resumingStores} onRetryResume={retryFailedStore} onSelectScenario={handleSelectScenario}/>
                    {
                        (storesStatus.every(s => s.status === "COMPLETED") && storesStatus.length > 0) && (
                        <span className="flex text-xs items-center gap-1 text-muted-foreground">
                            <Info className="w-5 h-5" />
                            La creación de carritos aún está en curso
                        </span>
                        )                
                    }

                </div>
            )
        }
        {
            remoteBrowserToken && remoteStoreBrowserStoreId && openBrowser && (
                <RemoteBrowser 
                    store_id={remoteStoreBrowserStoreId} 
                    storeName={getStoreBdData(remoteStoreBrowserStoreId, storeList)?.name ?? null}
                    storeLogo={getStoreBdData(remoteStoreBrowserStoreId, storeList)?.logo_url ?? null}
                    currentStoreStatus={storesStatus.find((storeStep) => storeStep.store_id === remoteStoreBrowserStoreId)?.status}
                    token={remoteBrowserToken}
                    streamingTtlUpdate={streamingTtlUpdate}
                    openState={openBrowser}
                    setOpen={closeBrowserHandler}
                    onConfirmSessionReady={confirmSessionReadyHandler}/>
            )
        }
        {
            data && storesStatus && openMissedItems && (
                <CartCreationMissedItems 
                    scenarioId={scenario_id}
                    status={storesStatus} 
                    openState={openMissedItems} 
                    setOpen={setOpenMissedItems} 
                    refreshTik={refetchTick}
                />
            )
        }
        </div>
    </main>

    <AlertDialog open={sessionExpired}>
        <AlertDialogContent>
            <AlertDialogHeader>
                <AlertDialogTitle>Sesión expirada</AlertDialogTitle>
                <AlertDialogDescription>
                    La sesión de creación de carrito ha finalizado. Es necesario refrescar la página para continuar.
                </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
                <AlertDialogAction onClick={() => window.location.reload()}>
                    Refrescar
                </AlertDialogAction>
            </AlertDialogFooter>
        </AlertDialogContent>
    </AlertDialog>
    </>    
    )
}

export const CartCreationLoading = () => {
    const isMobile = !useMediaQuery(isDesktopSize);

    return(
        <>
        <main>
            <div className={cn("flex flex-col gap-5 py-4 px-6 lg:p-8", {
                "w-full": isMobile,
                "w-2/5 mx-auto": !isMobile
            })}>

            {/* Skeleton: barra de estado del stream */}
            <MainLogo classname="h-16 mx-auto"/>
            <h2 className="text-lg font-semibold text-center text-muted-foreground animate-pulse">Creando tus carritos...</h2>

            {/* Skeleton: tarjetas de tienda */}
            {[1, 2, 3].map(i => (
                <div key={i} className="w-full flex items-center gap-4 p-4 border border-muted rounded-xl">
                    {/* Avatar */}
                    <Skeleton className="w-10 h-10 rounded-full shrink-0" />
                    {/* Contenido */}
                    <div className="flex flex-col gap-2 flex-1">
                        <Skeleton className="h-4 w-32 rounded" />
                        <Skeleton className="h-2 w-full rounded-full" />
                        <div className="flex gap-4">
                            <Skeleton className="h-3 w-20 rounded" />
                            <Skeleton className="h-3 w-16 rounded" />
                        </div>
                    </div>
                    {/* Botón */}
                    <Skeleton className="h-9 w-20 rounded-lg shrink-0" />
                </div>
            ))}

            </div>

        </main>
        </>
    )
} 

export const CartCreationError = ({error}:{error:string}) => {
    return(
        <>
        <main className="flex flex-row w-full gap-5 py-4 px-6  lg:p-8 flex-wrap">
            <p>Error: {error.toString()}</p>
        </main>
        </>
    )
}   
